import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

function Mark({ children, tone = 'text.secondary' }: { children: ReactNode; tone?: string }) {
  return (
    <Box aria-hidden sx={{ display: 'grid', placeItems: 'center', width: 44, height: 44, mx: 'auto', mb: 1.5, borderRadius: 1.5, border: 1, borderStyle: 'dashed', borderColor: 'divider', bgcolor: 'background.default', color: tone, '& svg': { fontSize: 22 } }}>
      {children}
    </Box>
  );
}

export function EmptyState({ title, action, icon }: { title: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
      <Mark>{icon ?? <InboxOutlinedIcon />}</Mark>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '46ch', mx: 'auto', mb: action ? 2 : 0 }}>{title}</Typography>
      {action}
    </Box>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Box sx={{ py: 6, px: 2, textAlign: 'center' }} role="alert">
      <Mark tone="error.main"><ErrorOutlineIcon /></Mark>
      <Typography variant="body2" sx={{ color: 'error.dark', maxWidth: '46ch', mx: 'auto', mb: onRetry ? 2 : 0 }}>{message}</Typography>
      {onRetry && <Button variant="outlined" onClick={onRetry}>Try again</Button>}
    </Box>
  );
}

export function LoadingBlock({ rows = 4 }: { rows?: number }) {
  return (
    <Stack sx={{ gap: 1, py: 1 }} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} variant="rounded" height={40} sx={{ opacity: 1 - i * (0.6 / rows) }} />)}
    </Stack>
  );
}

interface Gated { isLoading: boolean; isError: boolean; data?: unknown; refetch: () => unknown }

/** Loading and failure for a query, so a panel never shows "nothing here" while it is still asking or after it failed. */
export function QueryGate({ query, error, rows = 4, children }: { query: Gated; error: string; rows?: number; children: ReactNode }) {
  if (query.isLoading) return <LoadingBlock rows={rows} />;
  if (query.isError && query.data === undefined) return <ErrorState message={error} onRetry={() => void query.refetch()} />;
  return children;
}
